import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getSocket } from "@/lib/socket";

// Helper function to detect browser
function detectBrowser(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("chrome") && !userAgent.includes("edg")) {
    return "Chrome";
  } else if (userAgent.includes("edg")) {
    return "Edge";
  } else if (userAgent.includes("firefox")) {
    return "Firefox";
  } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
    return "Safari";
  }
  return "Unknown";
}

// Location is only updated once on app start, not repeatedly

interface LocationContextValue {
  isTracking: boolean;
  isPending: boolean;
  permission: PermissionState | null;
  lastUpdate: Date | null;
  startTracking: () => Promise<boolean>;
  stopTracking: () => Promise<void>;
  checkPermission: () => Promise<PermissionState | null>;
}

const LocationContext = createContext<LocationContextValue | null>(null);

let sharedWatchId: number | null = null;

export function LocationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isTracking, setIsTracking] = useState(false);
  const [permission, setPermission] = useState<PermissionState | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isPending, setIsPending] = useState(false);
  const hasAutoStartedRef = useRef(false); // Track if we've auto-started for this user

  // Check current permission status
  const checkPermission = useCallback(async () => {
    if (!("permissions" in navigator)) {
      return null;
    }

    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      setPermission(result.state);
      return result.state;
    } catch (error) {
      console.error("Error checking location permission:", error);
      return null;
    }
  }, []);

  // Update location on server using new geofencing endpoint and emit via sockets
  const updateLocation = useCallback(
    async (latitude: number, longitude: number) => {
      if (!user?.id) {
        return;
      }

      try {
        // Update location via HTTP API
        await apiRequest("POST", "/location/update", { latitude, longitude });
        setLastUpdate(new Date());
        
        // Also emit location update via socket for real-time updates
        const socket = getSocket();
        if (socket?.connected) {
          socket.emit("location:update", {
            userId: user.id,
            latitude,
            longitude,
            timestamp: new Date().toISOString(),
          });
        }
        
        // Invalidate location queries to update map view in real-time
        queryClient.invalidateQueries({ queryKey: ['/location/staff'] });
      } catch (error) {
        // Silently fail - don't interrupt user workflow with location errors
        console.debug("Location update failed (non-critical):", error);
      }
    },
    [user?.id]
  );

  // Request location permission and start tracking
  // ONLY AVAILABLE FOR STAFF - Owners/admins don't need location tracking
  const startTracking = useCallback(async () => {
    if (!user?.id) {
      toast({
        title: "Not authenticated",
        description: "Please log in to share your location.",
        variant: "destructive",
      });
      return false;
    }

    // Only staff can share location - owners/admins don't need this feature
    if (user.role !== 'staff') {
      toast({
        title: "Location tracking not available",
        description: "Location tracking is only available for staff members.",
        variant: "destructive",
      });
      return false;
    }

    if (!("geolocation" in navigator)) {
      toast({
        title: "Location not supported",
        description: "Your browser doesn't support location services.",
        variant: "destructive",
      });
      return false;
    }

    // If already tracking, don't start again
    if (sharedWatchId !== null) {
      setIsTracking(true);
      return true;
    }

    setIsPending(true);
    
    // Check permission status first
    let currentPermission: PermissionState | null = null;
    try {
      if ("permissions" in navigator) {
        const permissionResult = await navigator.permissions.query({ name: "geolocation" });
        currentPermission = permissionResult.state;
        setPermission(currentPermission);
        
        // If permission is denied, don't even try to get position
        if (currentPermission === "denied") {
          setIsPending(false);
          toast({
            title: "Location permission denied",
            description: "Please enable location permission in your browser settings to share your location.",
            variant: "destructive",
          });
          return false;
        }
      }
    } catch (permError) {
      // Continue anyway - some browsers don't support permissions API
    }

    try {
      // Request permission by getting current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true, // Use high accuracy for better tracking
          timeout: 15000, // Increased timeout
          maximumAge: 0, // Don't use cached position
        });
      });

      // Update server with initial location
      await updateLocation(position.coords.latitude, position.coords.longitude);

      // Enable location tracking on server
      await apiRequest("PATCH", `/users/${user.id}/location-enabled`, { enabled: true });

      // Start continuous location tracking using watchPosition
      const watchId = navigator.geolocation.watchPosition(
        async (newPosition) => {
          const lat = newPosition.coords.latitude;
          const lng = newPosition.coords.longitude;
          
          // Update location continuously
          await updateLocation(lat, lng);
        },
        (error) => {
          console.error("Geolocation watch error:", error);
          // Don't stop tracking on error, just log it
          // The watchPosition will continue to try
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );

      // Store watch ID so we can clear it later
      sharedWatchId = watchId;

      setIsTracking(true);
      await checkPermission();

      // Mark that we've started tracking for this session
      // This prevents auto-restart after manual disable
      hasAutoStartedRef.current = true;

      // Invalidate user query to refresh locationEnabled status
      await queryClient.invalidateQueries({ queryKey: ["/auth/user"] });
      await queryClient.invalidateQueries({ queryKey: ["/users"] });

      toast({
        title: "Location sharing enabled",
        description: "Your location is being shared in real-time with the team.",
      });

      setIsPending(false);
      return true;
    } catch (error: any) {
      setIsPending(false);
      
      let description = "Could not access your location. ";
      let title = "Location sharing failed";
      
      if (error.code === 1) {
        // Permission denied - this is expected user behavior, don't log as error
        // Permission denied - update permission state
        title = "Location permission denied";
        setPermission("denied");
        
        // Re-check permission to ensure state is updated
        await checkPermission();
        
        // Provide browser-specific instructions
        const browser = detectBrowser();
        let instructions = "Please enable location permission in your browser settings.";
        
        if (browser === "Chrome" || browser === "Edge") {
          instructions = "Click the lock icon in the address bar, then allow location access. Or go to Settings > Privacy and security > Site settings > Location.";
        } else if (browser === "Firefox") {
          instructions = "Click the shield icon in the address bar, then allow location access. Or go to Settings > Privacy & Security > Permissions > Location.";
        } else if (browser === "Safari") {
          instructions = "Go to Safari > Settings > Websites > Location, then allow access for this site.";
        }
        
        description = instructions;
      } else if (error.code === 2) {
        // Position unavailable
        title = "Location unavailable";
        description = "Your device could not determine your location. Please check your device's location settings and ensure location services are enabled.";
      } else if (error.code === 3) {
        // Timeout
        title = "Location request timed out";
        description = "The location request took too long. Please check your internet connection and try again.";
      } else {
        // Other error - check if permission was actually granted
        if (currentPermission === "granted") {
          // Permission is granted but we still got an error - might be a temporary issue
          description = "Could not get your location right now. Please try again in a moment.";
        } else {
          description = "Please enable location services in your device settings.";
        }
      }

      // Only log non-permission-denied errors to console
      if (error.code !== 1) {
      }

      toast({
        title,
        description,
        variant: "destructive",
        duration: 6000, // Show longer for denied permission
      });

      return false;
    }
  }, [user?.id, updateLocation, toast, checkPermission]);

  // Stop tracking
  const stopTracking = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    setIsPending(true);

    // Clear geolocation watch
    if (sharedWatchId !== null && typeof sharedWatchId === 'number') {
      navigator.geolocation.clearWatch(sharedWatchId);
      sharedWatchId = null;
    }

    try {
      // Disable location tracking on server
      await apiRequest("PATCH", `/users/${user.id}/location-enabled`, { enabled: false });

      setIsTracking(false);

      // Invalidate user query to refresh locationEnabled status
      await queryClient.invalidateQueries({ queryKey: ["/auth/user"] });
      await queryClient.invalidateQueries({ queryKey: ["/users"] });

      toast({
        title: "Location sharing disabled",
        description: "Your location is no longer being shared.",
      });
      
      setIsPending(false);
    } catch (error) {
      console.error("Failed to stop tracking:", error);
      setIsPending(false);
    }
  }, [user?.id, toast]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Listen for permission state changes (when user changes it in browser settings)
  useEffect(() => {
    if (!("permissions" in navigator)) {
      return;
    }

    let permissionStatus: PermissionStatus | null = null;

    const setupPermissionListener = async () => {
      try {
        const result = await navigator.permissions.query({ name: "geolocation" });
        permissionStatus = result;
        setPermission(result.state);

        // Listen for changes (e.g., user enables permission in browser settings)
        result.onchange = () => {
          setPermission(result.state);
          // If permission is granted and user wants tracking, auto-start
          // ONLY FOR STAFF - Owners/admins don't need location tracking
          if (result.state === "granted" && user?.locationEnabled && !isTracking && user?.role === 'staff') {
            // Don't auto-start if user manually disabled it
            if (!hasAutoStartedRef.current) {
              startTracking();
            }
          }
        };
      } catch (error) {
      }
    };

    setupPermissionListener();

    // Cleanup
    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [user?.locationEnabled, isTracking, startTracking, checkPermission]);

  // Auto-start tracking when user loads with locationEnabled=true
  // Only auto-starts once per user session to prevent re-enabling after manual disable
  // ONLY FOR STAFF - Owners/admins don't need location tracking
  useEffect(() => {
    if (!user?.id) {
      // User not loaded yet
      return;
    }

    // Only staff can share location
    if (user.role !== 'staff') {
      return;
    }

    // Only auto-start if we haven't already started (auto or manual)
    if (!hasAutoStartedRef.current) {
      if (user.locationEnabled && sharedWatchId === null && !isTracking) {
        // Auto-start for users with location enabled
        // Note: startTracking() will set hasAutoStartedRef.current = true
        startTracking();
      }
    }
  }, [user?.id, user?.role, user?.locationEnabled, isTracking, startTracking]);

  // Cleanup on logout: clear watch and reset state
  useEffect(() => {
    if (!user?.id) {
      // User logged out - clean up tracking state
      hasAutoStartedRef.current = false;
      setIsTracking(false);
      setPermission(null);
      setLastUpdate(null);
      
      // Clear geolocation watch
      if (sharedWatchId !== null && typeof sharedWatchId === 'number') {
        navigator.geolocation.clearWatch(sharedWatchId);
        sharedWatchId = null;
      }
    }
  }, [user?.id]);

  return (
    <LocationContext.Provider
      value={{
        isTracking,
        isPending,
        permission,
        lastUpdate,
        startTracking,
        stopTracking,
        checkPermission,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationTracking() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocationTracking must be used within LocationProvider");
  }
  return context;
}
